import { useEffect, useMemo, useRef } from "react";

import type { HitlDecide } from "@/api/types";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import {
  blockDisplayMode,
  buildHistoryBlocks,
  renderBlock,
} from "@/features/threads/components/ChatStream";
import {
  useThreadMessages,
  useThreadSteps,
} from "@/features/threads/hooks/useThreads";
import { useUiStore } from "@/stores/uiStore";

interface Props {
  threadId: string;
  /**
   * Quando true, polla messages/steps (execucao viva no worker --
   * tasks em background nao tem SSE aberto no cliente; o historico
   * persiste incrementalmente).
   */
  polling?: boolean;
  /**
   * Habilita decidir HITL inline pelo HitlBanner do transcript.
   * Ausente: banners pendentes aparecem desabilitados (so leitura).
   */
  onDecideHitl?: (hitlRequestId: string, payload: HitlDecide) => Promise<void>;
}

const POLL_INTERVAL_MS = 4000;

/**
 * Transcript somente-leitura de uma thread (sem composer, sem SSE).
 * Reusa a reconstrucao de historico + render de blocos do ChatStream
 * (MessageBubble/ThinkingBlock/ToolCallBlock/HitlBanner/...). Usado
 * pelo detalhe de Task em background.
 */
export function ThreadTranscript({ threadId, polling, onDecideHitl }: Props) {
  const refetchInterval = polling ? POLL_INTERVAL_MS : false;
  const messagesQuery = useThreadMessages(threadId, { refetchInterval });
  const stepsQuery = useThreadSteps(threadId, { refetchInterval });
  const showToolUsage = useUiStore((s) => s.chatShowToolUsage);

  const blocks = useMemo(
    () =>
      buildHistoryBlocks(
        messagesQuery.data ?? [],
        stepsQuery.data?.items ?? [],
      ),
    [messagesQuery.data, stepsQuery.data],
  );
  // tool_call/tool_result somem com o toggle off; um tool_result que gerou
  // arquivo vira "files-only" (so os baloes de arquivo, sempre visiveis).
  const visible = blocks
    .map((b) => ({ b, mode: blockDisplayMode(b, showToolUsage) }))
    .filter((x) => x.mode !== "hidden");

  // Auto-scroll "cola no fundo": acompanha o transcript crescendo no
  // polling, mas respeita o usuario que rolou para cima.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  useEffect(() => {
    const el = scrollRef.current;
    if (el && stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [visible.length]);

  function handleScroll() {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }

  if (messagesQuery.isLoading || stepsQuery.isLoading) {
    return <LoadingState />;
  }
  if (messagesQuery.isError || stepsQuery.isError) {
    return (
      <ErrorState
        error={messagesQuery.error ?? stepsQuery.error}
        onRetry={() => {
          void messagesQuery.refetch();
          void stepsQuery.refetch();
        }}
      />
    );
  }

  const decide = onDecideHitl ?? (() => Promise.resolve());
  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="flex-1 space-y-2 overflow-y-auto"
    >
      {visible.map(({ b, mode }) =>
        renderBlock(b, decide, undefined, mode === "files-only"),
      )}
    </div>
  );
}
